/**
 * MyGroupsList — the full list of the member's groups with client-side search
 * and sort, reached from the My Groups carousel's View All (mobile
 * myGroupsList parity).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { filterAndSortGroups } from '@wlb/shared/utils/filterGroups';
import type { Group, GroupSortOption } from '@wlb/shared/types/groups';
import { ChevronLeftIcon } from '../components/ui/icons';
import { ListStatus } from '../components/ui/ListStatus';
import { GroupRow } from '../features/groups/GroupRow';
import { GroupRowsSkeleton } from '../features/groups/GroupSkeletons';
import { GroupSearchBar } from '../features/groups/GroupSearchBar';
import { SortGroupsModal } from '../features/groups/SortGroupsModal';
import { useScrollRestore } from '../features/groups/useScrollRestore';
import '../components/ui/skeleton.css';
import '../features/feed/feed.css';
import '../features/groups/groups.css';

export function MyGroupsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    myGroups,
    myGroupsLoading,
    myGroupsError,
    myGroupsLoaded,
    fetchMyGroups,
    joinGroup,
  } = useGroup();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<GroupSortOption>('newest');
  const [sortOpen, setSortOpen] = useState(false);

  const load = useCallback(() => {
    fetchMyGroups().catch(() => undefined);
  }, [fetchMyGroups]);

  useEffect(load, [load]);

  /** Member-initiated reload — forced, since the cached list is what they doubt. */
  const refresh = useCallback(() => {
    fetchMyGroups(undefined, { force: true }).catch(() => undefined);
  }, [fetchMyGroups]);

  const filteredGroups = useMemo(
    () => filterAndSortGroups(myGroups, searchQuery, sortOption),
    [myGroups, searchQuery, sortOption],
  );

  // Reset scroll to top whenever the query or sort changes so a stale offset
  // from a previous result set is not restored into a shorter list.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [searchQuery, sortOption]);

  useScrollRestore('groups:myGroupsList', !myGroupsLoading || myGroups.length > 0);

  const handleSelect = (group: Group) => navigate(`/groups/${group.id}`);
  const handleJoin = (groupId: string) => { joinGroup(groupId).catch(() => undefined); };

  const renderList = () => {
    if (myGroupsLoading && myGroups.length === 0) {
      return <GroupRowsSkeleton />;
    }
    if (myGroupsError && myGroups.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={myGroupsError}
          onAction={refresh}
          busy={myGroupsLoading}
        />
      );
    }
    if (!myGroupsLoaded && myGroups.length === 0) {
      return <GroupRowsSkeleton />;
    }
    if (filteredGroups.length === 0) {
      // Search misses get no refresh: the list is current, the query is narrow.
      const searching = Boolean(searchQuery.trim());
      return (
        <ListStatus
          variant="empty"
          title={
            searching
              ? t('home.myGroups.emptySearchResults')
              : t('home.myGroups.emptyGroups')
          }
          onAction={searching ? undefined : refresh}
          busy={myGroupsLoading}
        />
      );
    }
    return (
      <div className="all-groups__list skeleton-fade-in">
        {filteredGroups.map(group => (
          <GroupRow key={group.id} group={group} onSelect={handleSelect} onJoin={handleJoin} />
        ))}
      </div>
    );
  };

  return (
    <section className="my-groups-list" aria-labelledby="my-groups-list-title">
      <header className="create-group__header">
        <button type="button" className="create-group__back" onClick={() => navigate(-1)}>
          <ChevronLeftIcon size={20} />
          <span id="my-groups-list-title">{t('home.groupTabs.myGroups')}</span>
        </button>
      </header>

      <div className="my-groups__header">
        <GroupSearchBar
          inputId="my-groups-list-search"
          value={searchQuery}
          onChange={setSearchQuery}
          onFilter={() => setSortOpen(true)}
        />
      </div>

      {renderList()}

      {sortOpen && (
        <SortGroupsModal
          selected={sortOption}
          onClose={() => setSortOpen(false)}
          onApply={setSortOption}
        />
      )}
    </section>
  );
}
